import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { customerRisk } from '../services/api';
import { AlertTriangle, Users } from 'lucide-react';

interface RiskCustomer {
  customer_key: string;
  customer_name: string;
  phone: string | null;
  email: string | null;
  total_cod_orders: number;
  resolved: number;
  delivered: number;
  voided: number;
  rto: number;
  stale_fulfilled: number;
  risk_pct: number | null;
  flagged: boolean;
  distinct_names: number;
  names: string[];
  multi_name_flag: boolean;
}

interface CustomerOrder {
  order_name: string;
  status: string;
  payment_method: string;
  total: number;
  created_at: string | null;
}

interface DailyOrder {
  order_name: string;
  customer_key: string | null;
  customer_name: string;
  phone: string | null;
  email: string | null;
  total: number;
  status: string;
  created_at: string | null;
  resolved: number;
  risk_pct: number | null;
  flagged: boolean;
  insufficient_history: boolean;
  distinct_names: number;
  names: string[];
  multi_name_flag: boolean;
}

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-card)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-xl)', padding: 0, overflow: 'hidden',
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  borderBottom: active ? '2px solid var(--accent-red)' : '2px solid transparent',
  color: active ? 'var(--text-primary)' : 'var(--text-muted)',
});

const todayStr = () => new Date().toISOString().slice(0, 10);

const CustomerRisk: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState<'flagged' | 'daily'>(searchParams.get('view') === 'daily' ? 'daily' : 'flagged');
  const [customers, setCustomers] = useState<RiskCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [orders, setOrders] = useState<Record<string, CustomerOrder[]>>({});

  const [dailyDate, setDailyDate] = useState(todayStr());
  const [dailyOrders, setDailyOrders] = useState<DailyOrder[]>([]);
  const [dailyLoading, setDailyLoading] = useState(false);

  const load = async (all: boolean) => {
    setLoading(true);
    const res = await customerRisk.list(all);
    setCustomers(res.data.customers);
    setLoading(false);
  };

  useEffect(() => { if (view === 'flagged') load(showAll); }, [showAll, view]);

  const loadDaily = async (date: string) => {
    setDailyLoading(true);
    const res = await customerRisk.getDaily(date);
    setDailyOrders(res.data.orders);
    setDailyLoading(false);
  };

  useEffect(() => { if (view === 'daily') loadDaily(dailyDate); }, [dailyDate, view]);

  const toggleExpand = async (key: string) => {
    if (expanded === key) { setExpanded(null); return; }
    setExpanded(key);
    if (!orders[key]) {
      const res = await customerRisk.getOrders(key);
      setOrders(prev => ({ ...prev, [key]: res.data.orders }));
    }
  };

  return (
    <Layout title="COD Risk">
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        <div style={tabStyle(view === 'flagged')} onClick={() => setView('flagged')}>Flagged Customers</div>
        <div style={tabStyle(view === 'daily')} onClick={() => setView('daily')}>Daily New Orders</div>
      </div>

      {view === 'flagged' && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Customers with 3+ resolved COD orders (flagged = more than half ended voided or RTO),
          plus any phone/email used under 2+ different names (flagged for likely fraud evasion).
        </div>
        <label style={{ fontSize: 13, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
          Show all (not just flagged)
        </label>
      </div>
      )}

      {view === 'daily' && (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          New COD orders placed on this date, flagged if the customer already has a risky history
          or if the phone/email has been used under 2+ different names.
        </div>
        <input
          type="date"
          value={dailyDate}
          max={todayStr()}
          onChange={e => setDailyDate(e.target.value)}
          style={{
            padding: '6px 10px', fontSize: 13, borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-primary)',
          }}
        />
      </div>
      )}

      {view === 'flagged' && (
      <div style={cardStyle}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg-elevated)', textAlign: 'left' }}>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Customer</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Phone</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>COD Orders</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Delivered</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Voided</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>RTO</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Risk %</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Names</th>
            </tr>
          </thead>
          <tbody>
            {!loading && customers.length === 0 && (
              <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
                No {showAll ? '' : 'flagged '}customers found.
              </td></tr>
            )}
            {customers.map(c => (
              <React.Fragment key={c.customer_key}>
                <tr
                  onClick={() => toggleExpand(c.customer_key)}
                  style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                >
                  <td style={{ padding: '12px 16px', color: 'var(--text-primary)', fontWeight: 600 }}>
                    {c.flagged && <AlertTriangle size={13} color="var(--accent-red)" style={{ marginRight: 6, verticalAlign: 'middle' }} />}
                    {c.customer_name}
                  </td>
                  <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{c.phone || c.email}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{c.total_cod_orders}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{c.delivered}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{c.voided}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{c.rto + c.stale_fulfilled}</td>
                  <td style={{ padding: '12px 16px', color: c.flagged ? 'var(--accent-red)' : 'var(--text-secondary)', fontWeight: c.flagged ? 700 : 400 }}>
                    {c.risk_pct === null ? '—' : `${c.risk_pct}%`}
                  </td>
                  <td
                    style={{ padding: '12px 16px', color: c.multi_name_flag ? '#d97706' : 'var(--text-secondary)', fontWeight: c.multi_name_flag ? 700 : 400 }}
                    title={c.names.join(', ')}
                  >
                    {c.multi_name_flag && <Users size={13} color="#d97706" style={{ marginRight: 6, verticalAlign: 'middle' }} />}
                    {c.distinct_names}
                  </td>
                </tr>
                {expanded === c.customer_key && (
                  <tr>
                    <td colSpan={8} style={{ padding: '0 16px 16px', background: 'var(--bg-elevated)' }}>
                      {!orders[c.customer_key] ? (
                        <div style={{ padding: 12, fontSize: 12, color: 'var(--text-muted)' }}>Loading orders…</div>
                      ) : (
                        <table style={{ width: '100%', fontSize: 12, marginTop: 8 }}>
                          <thead>
                            <tr style={{ textAlign: 'left', color: 'var(--text-muted)' }}>
                              <th style={{ padding: '6px 8px' }}>Order</th>
                              <th style={{ padding: '6px 8px' }}>Date</th>
                              <th style={{ padding: '6px 8px' }}>Payment</th>
                              <th style={{ padding: '6px 8px' }}>Status</th>
                              <th style={{ padding: '6px 8px' }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders[c.customer_key].map(o => (
                              <tr key={o.order_name} style={{ color: 'var(--text-secondary)' }}>
                                <td style={{ padding: '6px 8px' }}>{o.order_name}</td>
                                <td style={{ padding: '6px 8px' }}>{o.created_at ? o.created_at.slice(0, 10) : ''}</td>
                                <td style={{ padding: '6px 8px' }}>{o.payment_method}</td>
                                <td style={{ padding: '6px 8px' }}>{o.status}</td>
                                <td style={{ padding: '6px 8px' }}>₹{o.total}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {view === 'daily' && (
      <div style={cardStyle}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--bg-elevated)', textAlign: 'left' }}>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Order</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Customer</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Phone</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Total</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>History</th>
              <th style={{ padding: '12px 16px', color: 'var(--text-muted)', fontWeight: 600 }}>Risk</th>
            </tr>
          </thead>
          <tbody>
            {!dailyLoading && dailyOrders.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
                No new COD orders on this date.
              </td></tr>
            )}
            {dailyOrders.map(o => (
              <tr key={o.order_name} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '12px 16px', color: 'var(--text-primary)', fontWeight: 600 }}>
                  {o.flagged && <AlertTriangle size={13} color="var(--accent-red)" style={{ marginRight: 6, verticalAlign: 'middle' }} />}
                  {o.order_name}
                </td>
                <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }} title={o.multi_name_flag ? o.names.join(', ') : undefined}>
                  {o.multi_name_flag && <Users size={13} color="#d97706" style={{ marginRight: 6, verticalAlign: 'middle' }} />}
                  {o.customer_name}
                </td>
                <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>{o.phone || o.email}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>₹{o.total}</td>
                <td style={{ padding: '12px 16px', color: 'var(--text-secondary)' }}>
                  {o.insufficient_history ? `${o.resolved} resolved (new)` : `${o.resolved} resolved`}
                </td>
                <td style={{
                  padding: '12px 16px', fontWeight: o.flagged ? 700 : 400,
                  color: o.flagged ? 'var(--accent-red)' : (o.insufficient_history ? 'var(--text-muted)' : 'var(--text-secondary)'),
                }}>
                  {o.insufficient_history ? 'Insufficient history' : `${o.risk_pct}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </Layout>
  );
};

export default CustomerRisk;
